import React, { useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Footer from './Footer';
import axios from 'axios';
import { 
  House, 
  Kanban, 
  Calendar, 
  CheckSquare,
  SignOut,
  Microphone
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const heartbeatRef = useRef(null);

  // Session heartbeat — sends a ping every 60s to track active time
  useEffect(() => {
    if (!user) return;
    const sendHeartbeat = () => {
      axios.post(`${API_URL}/api/heartbeat`, {}, { withCredentials: true }).catch(() => {});
    };
    sendHeartbeat(); // initial
    heartbeatRef.current = setInterval(sendHeartbeat, 60000);
    return () => clearInterval(heartbeatRef.current);
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/', label: 'Home', icon: House },
    { path: '/pipeline', label: 'Deals', icon: Kanban },
    { path: '/meetings', label: 'Meetings', icon: Calendar },
    { path: '/tasks', label: 'Tasks', icon: CheckSquare },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  // Don't show FAB on voice recording pages
  const showFab = !location.pathname.includes('/voice') && !location.pathname.includes('/new');

  return (
    <div className="min-h-screen bg-[#F5F5F7] overflow-x-hidden">
      {/* Apple-style Header */}
      <header className="header-glass sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-14">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <span className="text-xl brand-name">
                Zelvrix
              </span>
            </Link>

            {/* Center Nav - Desktop */}
            <nav className="hidden md:flex items-center gap-1 bg-[#F5F5F7] rounded-full p-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                      active
                        ? 'bg-white text-[#1D1D1F] shadow-sm'
                        : 'text-[#86868B] hover:text-[#1D1D1F]'
                    }`}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                  >
                    <Icon size={18} weight={active ? 'fill' : 'regular'} />
                    <span className="hidden lg:inline">{item.label}</span>
                  </Link>
                );
              })}
            </nav>

            {/* Right Section */}
            <div className="flex items-center gap-3">
              {/* User avatar */}
              <button 
                onClick={handleLogout}
                className="flex items-center gap-2 text-[#86868B] hover:text-[#1D1D1F] transition-colors"
                data-testid="logout-btn"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#5856D6] to-[#AF52DE] flex items-center justify-center text-white text-sm font-semibold">
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </div>
                <SignOut size={18} className="hidden sm:block" />
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Bottom Nav */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-xl border-t border-gray-200/50 z-40" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
          <nav className="flex items-center justify-around py-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex flex-col items-center gap-1 py-1 transition-colors ${
                    active ? 'text-[#007AFF]' : 'text-[#86868B]'
                  }`}
                >
                  <Icon size={22} weight={active ? 'fill' : 'regular'} />
                  <span className="text-[10px] font-medium">{item.label}</span>
                </Link>
              );
            })}
            <button
              onClick={handleLogout}
              className="flex flex-col items-center gap-1 py-1 text-[#86868B] active:text-[#FF3B30] transition-colors"
              data-testid="mobile-logout-btn"
            >
              <SignOut size={22} />
              <span className="text-[10px] font-medium">Logout</span>
            </button>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-6 md:pb-6" style={{ paddingBottom: 'calc(5rem + env(safe-area-inset-bottom, 0px))' }}>
        <div className="page-enter">
          {children}
        </div>
      </main>

      {/* Footer */}
      <Footer />

      {/* Floating Record Button */}
      {showFab && (
        <Link to="/meetings/voice" className="fab-record" data-testid="fab-record">
          <Microphone size={28} weight="bold" className="text-white" />
        </Link>
      )}
    </div>
  );
}
